/* eslint-disable complexity */
import { lowerCase } from 'lodash';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { getState } from 'src/store';
import axios from 'src/workspace/axios';
import { getSelectedTileData } from './selector';
import {
  setError,
  setIsLoading,
  setSelectedTileData,
  setTileActionError,
  setTileActionLoading,
  setWidgetData,
  setWidgetState,
} from './slice';
import { SelectedTileData } from './types';

const getUrl = (moduleCode, locationCode, itemCode, widgetCode) => {
  if (moduleCode && locationCode && itemCode && widgetCode) {
    return `/v1/modules/${moduleCode}/locations/${locationCode}/items/${itemCode}/widgets/${widgetCode}`;
  }
  if (moduleCode && locationCode && widgetCode) {
    return `/v1/modules/${moduleCode}/locations/${locationCode}/default/widgets/${widgetCode}`;
  }
  if (locationCode && itemCode && widgetCode) {
    return `/v1/global/locations/${locationCode}/items/${itemCode}/widgets/${widgetCode}`;
  }
  return `/v1/global/locations/${locationCode}/default/widgets/${widgetCode}`;
};

export const getSpgWidgetData = createAsyncThunk(
  'get/spg-widget-data',
  async (
    {
      moduleCode,
      locationCode = 'home',
      itemCode,
      widgetCode = 'marketing_home',
    }: {
      moduleCode?: string;
      locationCode?: string;
      itemCode?: string;
      widgetCode?: string;
    },
    { dispatch },
  ) => {
    try {
      dispatch(setIsLoading({ widgetCode, data: true }));
      dispatch(setError({ widgetCode, data: '' }));
      const response: any = await axios.get(
        getUrl(moduleCode, locationCode, itemCode, widgetCode),
      );

      dispatch(setWidgetData({ widgetCode, data: response.data }));
      dispatch(setIsLoading({ widgetCode, data: false }));
    } catch (error: any) {
      // eslint-disable-next-line
      console.error(error);
      dispatch(setIsLoading({ widgetCode, data: false }));
      dispatch(
        setError({
          widgetCode,
          data: error?.message || 'Failed to fetch SPG widget data',
        }),
      );
    }
  },
);

export const fetchTileAction = createAsyncThunk(
  'get/spg-tile-data',
  async (
    {
      widgetCode,
      tile,
    }: {
      widgetCode: string;
      tile: Record<string, any>;
    },
    { dispatch },
  ) => {
    try {
      const { links } = tile;
      const { href, method, body } = links.start ?? links.next;

      dispatch(setTileActionLoading({ widgetCode, data: true }));
      dispatch(setTileActionError({ widgetCode, data: '' }));
      dispatch(setSelectedTileData({ widgetCode, data: {} }));
      const response: any = await axios[lowerCase(method)](href, { body });
      if (response?.data?.error) {
        dispatch(setTileActionLoading({ widgetCode, data: false }));
        dispatch(
          setTileActionError({
            widgetCode,
            data: response?.data?.error || 'Failed to fetch Tile Action',
          }),
        );
      } else {
        dispatch(setTileActionLoading({ widgetCode, data: false }));
        dispatch(setSelectedTileData({ widgetCode, data: response.data }));
      }
    } catch (error: any) {
      // eslint-disable-next-line
      console.error(error);
      dispatch(setTileActionLoading({ widgetCode, data: false }));
      dispatch(
        setTileActionError({
          widgetCode,
          data: error?.message || 'Failed to fetch Tile Action',
        }),
      );
    }
  },
);

export const saveCaptureDetails = createAsyncThunk(
  'post/capture-details',
  async (
    {
      widgetCode,
      payload = {},
    }: {
      widgetCode: string;
      payload: any;
    },
    { dispatch },
  ) => {
    try {
      dispatch(setTileActionLoading({ widgetCode, data: true }));
      dispatch(setTileActionError({ widgetCode, data: '' }));
      const { links } = getSelectedTileData(
        getState(),
        widgetCode,
      ) as SelectedTileData;
      if (!links) {
        dispatch(setTileActionLoading({ widgetCode, data: false }));
        throw new Error('Links data is missing');
      }
      const targetLink = links.start || links.next;
      if (!targetLink) {
        dispatch(setTileActionLoading({ widgetCode, data: false }));
        throw new Error('Neither start nor next link is available');
      }
      const { body, metadata, method, href } = targetLink;
      const response: any = await axios[lowerCase(method)](href, {
        ...body,
        ...payload,
        metadata,
      });
      if (response?.data?.error) {
        dispatch(setTileActionLoading({ widgetCode, data: false }));
        dispatch(
          setTileActionError({
            widgetCode,
            data: response?.data?.error || 'Failed to Capture Details Action',
          }),
        );
      } else {
        dispatch(setTileActionLoading({ widgetCode, data: false }));
        dispatch(
          setSelectedTileData({ widgetCode, data: response?.data || {} }),
        );
      }
    } catch (error: any) {
      // eslint-disable-next-line
      console.error(error);
      dispatch(setTileActionLoading({ widgetCode, data: false }));
      dispatch(
        setTileActionError({
          widgetCode,
          data: error?.message || 'Failed to fetch Tile Action data',
        }),
      );
    }
  },
);

const onActivate = ({ dispatch, props }) => {
  const { moduleCode, locationCode, itemCode, widgetCode } = props;
  dispatch(
    setWidgetState({
      widgetCode,
      data: {
        data: {},
        selectedTileData: {},
        isLoading: false,
        error: '',
        isTileActionLoading: false,
        tileActionError: '',
      },
    }),
  );
  dispatch(
    getSpgWidgetData({ moduleCode, locationCode, itemCode, widgetCode }),
  );
};

const onDeactivate = () => {};

export default { onActivate, onDeactivate };
